import { useCallback, useEffect, useRef, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";

// #3570 (show-setup workstream) -- the Score Admin dashboard's Pre-show view
// reads ONE call, GET /show/:showId/setup (#3569): the show's Pre-show/started
// state, the 7-item Setup checklist, the Setup progress percent and the Good to
// know settings. The three writes (Start show, Back to pre-show, hand check)
// each answer with the same shape, so the screen swaps in the server's answer
// instead of guessing.
//
// Polls on the dashboard's own cadence (20 s): pauses while the tab is hidden,
// refreshes the moment it is visible again. A failed poll keeps the last good
// answer (the dashboard never blanks on a blip).
export const SETUP_POLL_MS = 20000;

export function useShowSetup(showId) {
  const scoreApi = useScoreApi();
  const apiRef = useRef(scoreApi);
  apiRef.current = scoreApi;
  const showIdRef = useRef(showId);
  showIdRef.current = showId;
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(showId != null);
  const [error, setError] = useState(null);

  // A show switch drops the previous show's answer at once, so its checklist
  // is never shown under the new show's heading.
  useEffect(() => {
    setSetup(null);
    setError(null);
    setLoading(showId != null);
  }, [showId]);

  const refresh = useCallback(async () => {
    const id = showIdRef.current;
    if (!id) return;
    try {
      const next = unwrap(await apiRef.current.shows.getShowSetup(id));
      if (showIdRef.current !== id) return;
      setSetup(next);
      setError(null);
    } catch (err) {
      if (showIdRef.current === id) setError(err);
    } finally {
      if (showIdRef.current === id) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!showId) return undefined;
    if (document.visibilityState !== "hidden") refresh();
    else setLoading(false);
    let timer = null;
    const start = () => {
      if (document.visibilityState === "hidden") return;
      if (timer == null) timer = setInterval(refresh, SETUP_POLL_MS);
    };
    const stop = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") stop();
      else {
        refresh();
        start();
      }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [showId, refresh]);

  // Each write resolves with the server's new setup answer. A failure throws to
  // the caller, which shows it next to the button that asked.
  const write = useCallback(async (call) => {
    const id = showIdRef.current;
    const next = unwrap(await call(apiRef.current.shows, id));
    if (showIdRef.current === id) {
      setSetup(next);
      setError(null);
    }
    return next;
  }, []);

  const startShow = useCallback(() => write((shows, id) => shows.startShowSetup(id)), [write]);
  const backToPreShow = useCallback(() => write((shows, id) => shows.backToPreShowSetup(id)), [write]);
  const setHandCheck = useCallback(
    (itemKey, checked) =>
      write((shows, id) => shows.setShowSetupHandCheck(id, itemKey, { checked })),
    [write]
  );

  return { setup, loading, error, refresh, startShow, backToPreShow, setHandCheck };
}

export default useShowSetup;
